import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import { GlassPanel } from "../components/GlassPanel";
import {
  ArrowLeft, MapPin, CalendarClock, DollarSign, ClipboardList, User,
  Wrench, FileText, Package, Tag, Sparkles, Pencil, ShieldCheck, Download,
  Send, Plus, ExternalLink,
} from "lucide-react";
import { CustomerCard, MultiItemBreakdown } from "../components/DetailsModal";
import JobPhotos from "../components/JobPhotos";
import JobStageAssignments from "../components/JobStageAssignments";
import JobTimeline from "../components/JobTimeline";
import StatusPipeline from "../components/StatusPipeline";
import SendInvoiceModal from "../components/SendInvoiceModal";
import { jobTypeMeta } from "../lib/jobTypes";
import { toast } from "sonner";
import { relativeTime, absoluteTime } from "../lib/utils";
import { useAuth, usePermission } from "../context/AuthContext";

/**
 * Full-page detail view for a single job. Replaces the popup DetailsModal
 * click flow inside the Jobs list — click a job → route to /jobs/:id and
 * see everything (customer, items, price, schedule, materials, photos,
 * notes) laid out with room to breathe.
 */
export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Pricing visibility now driven by the RBAC permission (view_pricing).
  // Falls back to role-based check for backwards-compat when the
  // /auth/me/full endpoint isn't deployed yet.
  const canSeePricing = usePermission("view_pricing");
  const hidePricing = !canSeePricing;
  // Only office/admin can edit a job record from the detail page. Installers
  // still update stage/timeline/photos inline; the full-form edit is a
  // receptionist action (customer, pricing, items, materials).
  const canEditJob = user?.role === "admin" || user?.role === "office";
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Invoices linked to this job (any doc in /invoices with quote_id === job.id).
  // Loaded alongside the job so the sidebar can list existing invoices and
  // offer a "create + email" CTA when none exist yet.
  const [invoices, setInvoices] = useState([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/jobs/${id}`);
      setRecord(data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load job");
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Refetch invoices attached to this job. Separate from the main record
  // load so "Create + send invoice" can refresh only the invoice list
  // without flickering the whole page.
  const loadInvoices = useCallback(async () => {
    if (!canSeePricing) return;
    try {
      const { data } = await api.get("/invoices");
      setInvoices((Array.isArray(data) ? data : []).filter((inv) => inv.quote_id === id));
    } catch (_) { /* leave stale */ }
  }, [id, canSeePricing]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const createInvoice = async () => {
    if (!record) return;
    const items = record.job_items || [];
    const itemsSum = items.reduce((s, it) => {
      const insts = it.installations || [];
      return s + (insts.length > 0
        ? insts.reduce((ss, inst) => ss + (Number(inst.price) || 0), 0)
        : (Number(it.price) || 0));
    }, 0);
    const override = Number(record.amount_override) || 0;
    const total = override > 0 ? override : (itemsSum > 0 ? itemsSum : Number(record.amount) || 0);
    if (!total || total <= 0) {
      toast.error("Set a price on this job before creating an invoice.");
      return;
    }
    setCreatingInvoice(true);
    try {
      const { data } = await api.post(`/quotes/${record.id}/convert-to-invoice`);
      toast.success("Invoice created — open the Send box to email it");
      await loadInvoices();
      setSendingInvoice(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create invoice");
    } finally {
      setCreatingInvoice(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-6 text-slate-400 text-sm italic" data-testid="job-detail-loading">
        Loading job…
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="max-w-5xl mx-auto p-6 space-y-4">
        <BackLink onClick={() => navigate("/jobs")} />
        <GlassPanel className="!p-8 text-center">
          <div className="text-red-300 text-sm mb-2">{error || "Job not found"}</div>
          <button
            onClick={() => navigate("/jobs")}
            className="text-xs uppercase tracking-widest text-slate-400 hover:text-white"
          >
            Return to jobs list
          </button>
        </GlassPanel>
      </div>
    );
  }

  const customer = {
    name: record.contact_name || record.name || "—",
    email: record.contact_email || record.email || "",
    phone: record.contact_phone || record.phone || "",
    address: record.contact_address || record.address || "",
  };
  const itemsSum = (record.job_items || []).reduce((s, it) => {
    const insts = it.installations || [];
    return s + (insts.length > 0
      ? insts.reduce((ss, inst) => ss + (Number(inst.price) || 0), 0)
      : (Number(it.price) || 0));
  }, 0);
  const override = Number(record.amount_override) || 0;
  const displayAmount = override > 0 ? override : (itemsSum > 0 ? itemsSum : Number(record.amount) || 0);

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-4" data-testid="job-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <BackLink onClick={() => navigate("/jobs")} />
        <div className="flex items-center gap-2">
          {record.created_at && (
            <span
              className="text-[11px] text-slate-500"
              title={absoluteTime(record.created_at)}
            >
              Created {relativeTime(record.created_at)}
            </span>
          )}
          {canEditJob && (
            <>
              <button
                type="button"
                data-testid="job-detail-customer-link"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/jobs/${record.id}/booking-link`);
                    try {
                      await navigator.clipboard.writeText(data.booking_url);
                      toast.success("Customer booking link copied — paste it into a text or email");
                    } catch {
                      // Fallback: just open the link so admin can grab it manually
                      window.open(data.booking_url, "_blank");
                      toast.success("Customer booking link opened");
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Couldn't build booking link");
                  }
                }}
                title="Copy a link your customer can open to see their booking"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/30 text-emerald-200 px-3 py-1.5 rounded-full transition"
              >
                <ExternalLink className="w-3 h-3" /> Customer view
              </button>
              <button
                type="button"
                data-testid="job-detail-edit"
                onClick={() => navigate(`/jobs?edit=${record.id}`)}
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest bg-white/5 hover:bg-white/10 border border-white/10 text-white px-3 py-1.5 rounded-full transition"
              >
                <Pencil className="w-3 h-3" /> Edit
              </button>
            </>
          )}
        </div>
      </div>

      {/* Title row */}
      <GlassPanel className="!p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-2">
              <Wrench className="w-3 h-3" /> Job
              {record.sub_status && (
                <span className="pill pill-info text-[9px]">{record.sub_status.replace(/_/g, " ")}</span>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-heading text-white mt-1">
              {record.vehicle || customer.name || `Job ${String(record.id).slice(0, 6)}`}
            </h1>
            {record.service && (
              <div className="text-sm text-slate-400 mt-0.5">{record.service}</div>
            )}
          </div>
          {!hidePricing && (
            <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-4 py-3" data-testid="job-detail-amount-tile">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                {override > 0 ? "Override total" : itemsSum > 0 ? "Auto total" : "Amount"}
              </div>
              <div className="text-lg font-heading text-white">${displayAmount.toLocaleString()}</div>
            </div>
          )}
        </div>
      </GlassPanel>

      {/* Two-column layout on wide screens: left = customer + items + notes; right = schedule + materials */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <GlassPanel className="!p-5 space-y-4" data-testid="job-detail-customer-panel">
            <CustomerCard customer={customer} contactId={record.contact_id} onClose={() => {}} />
          </GlassPanel>

          {Array.isArray(record.job_items) && record.job_items.length > 0 && (
            <GlassPanel className="!p-5" data-testid="job-detail-items-panel">
              <MultiItemBreakdown
                items={record.job_items}
                jobId={record.id}
                kind="job"
                record={record}
                onRefresh={load}
              />
            </GlassPanel>
          )}

          {/* Legacy single-item view (only if no job_items) */}
          {(!record.job_items || record.job_items.length === 0) && (
            <GlassPanel className="!p-5 space-y-3">
              <SectionHead icon={ClipboardList} title="What's being wrapped" />
              {record.vehicle && <KV label="Item" value={record.vehicle} />}
              {record.service && <KV label="Service" value={record.service} />}
              {record.job_type && <KV label="Type" value={jobTypeMeta(record.job_type).label} />}
              <div className="pt-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Photos</div>
                <JobPhotos job={record} onUpdate={load} />
              </div>
            </GlassPanel>
          )}

          {record.notes && (
            <GlassPanel className="!p-5 space-y-2" data-testid="job-detail-notes-panel">
              <SectionHead icon={FileText} title="Notes" />
              <p className="text-sm text-slate-300 whitespace-pre-wrap">{record.notes}</p>
            </GlassPanel>
          )}

          {/* Staff-per-stage editor — adaptive to this job's actual pipeline
              (only stages relevant to the picked installations are listed).
              Hidden from installers; office + admin can rebalance ownership
              from here without hunting through the list modal. */}
          {(user?.role === "admin" || user?.role === "office") && record.main_status === "job" && (
            <GlassPanel className="!p-5 space-y-3" data-testid="job-detail-staff-panel">
              <JobStageAssignments
                job={record}
                canEdit={true}
                onChange={load}
              />
            </GlassPanel>
          )}

          {record.inspection_notes && (
            <GlassPanel className="!p-5 space-y-2">
              <SectionHead icon={Sparkles} title="Inspection notes" />
              <p className="text-sm text-slate-300 whitespace-pre-wrap">{record.inspection_notes}</p>
            </GlassPanel>
          )}

          {/* Timeline — chronological feed of stage moves, assignment
              changes, and staff comments. Visible to everyone who can see
              the job (installer/assistant included) so they can leave notes
              and see who progressed which stage. */}
          <GlassPanel className="!p-5" data-testid="job-detail-timeline-panel">
            <JobTimeline
              job={record}
              canEdit={
                user?.role === "admin" ||
                user?.role === "office" ||
                record.assigned_to === user?.id ||
                (record.assigned_installer_ids || []).includes(user?.id) ||
                (record.assigned_assistant_ids || []).includes(user?.id) ||
                (record.timeline || []).some((s) => s.installer_id === user?.id)
              }
              onChange={load}
            />
          </GlassPanel>
        </div>

        <div className="space-y-4">
          {/* Payment status panel — admin/office only. Uses the same
              payment_summary the Jobs list card renders, so numbers stay
              in sync without an extra fetch. */}
          {canSeePricing && record.payment_summary && record.payment_summary.status !== "no_invoice" && (
            <GlassPanel className="!p-5 space-y-3" data-testid="job-detail-payment-panel">
              <SectionHead icon={DollarSign} title="Payment" />
              <PaymentDetailPanel summary={record.payment_summary} />
            </GlassPanel>
          )}

          {/* Invoice panel — admin/office can create + email an invoice
              for this job listing every installation as a line item, or
              re-send an existing invoice from here without leaving the
              job detail page. */}
          {canSeePricing && canEditJob && (
            <GlassPanel className="!p-5 space-y-3" data-testid="job-detail-invoice-panel">
              <SectionHead icon={FileText} title="Invoice" />
              {invoices.length === 0 ? (
                <>
                  <div className="text-xs text-slate-400 leading-relaxed">
                    Turn the work on this job into an invoice with each installation as a line item. You can preview the line items, generate a payment link and email it to the customer in one flow.
                  </div>
                  <button
                    type="button"
                    onClick={createInvoice}
                    disabled={creatingInvoice}
                    data-testid="job-detail-create-invoice"
                    className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-black text-xs font-semibold rounded-full px-4 py-2 transition"
                  >
                    <Plus className="w-3 h-3" /> {creatingInvoice ? "Creating…" : "Create + send invoice"}
                  </button>
                </>
              ) : (
                <>
                  <ul className="space-y-2">
                    {invoices.map((inv) => (
                      <li
                        key={inv.id}
                        data-testid={`job-detail-invoice-row-${inv.id}`}
                        className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 space-y-1.5"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-white text-sm font-medium truncate">
                              ${Number(inv.amount || 0).toLocaleString()}
                            </div>
                            <div className="text-[11px] text-slate-500 truncate">
                              {inv.status === "paid" ? "Paid" : inv.status === "sent" ? "Sent" : "Unpaid"}
                              {inv.created_at && ` · ${new Date(inv.created_at).toLocaleDateString()}`}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {inv.payment_url && (
                              <a
                                href={inv.payment_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`job-detail-invoice-view-${inv.id}`}
                                title="Open the customer payment page"
                                className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => setSendingInvoice(inv)}
                              data-testid={`job-detail-invoice-send-${inv.id}`}
                              title="Email this invoice to the customer"
                              className="p-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300"
                            >
                              <Send className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        {Array.isArray(inv.work_summary) && inv.work_summary.length > 0 && (
                          <ul className="pl-2 border-l border-white/10 space-y-1">
                            {inv.work_summary.map((line, i) => (
                              <li key={i} className="text-[11px] text-slate-400">
                                <div className="text-slate-300 truncate">{line.title}</div>
                                {line.detail && <div className="truncate">{line.detail}</div>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={createInvoice}
                    disabled={creatingInvoice}
                    data-testid="job-detail-create-invoice"
                    className="w-full inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-slate-200 text-xs font-medium rounded-full px-4 py-2 border border-white/10 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-3 h-3" /> {creatingInvoice ? "Creating…" : "New invoice"}
                  </button>
                </>
              )}
            </GlassPanel>
          )}

          <GlassPanel className="!p-5 space-y-2" data-testid="job-detail-schedule-panel">
            <SectionHead icon={CalendarClock} title="Schedule" />
            {record.scheduled_date && <KV label="Start" value={new Date(record.scheduled_date).toLocaleString()} />}
            {record.scheduled_end_date && <KV label="End" value={new Date(record.scheduled_end_date).toLocaleString()} />}
            {(() => {
              // Prefer the multi-crew arrays; fall back to legacy single field.
              const installers = (record.assigned_installer_names && record.assigned_installer_names.length > 0)
                ? record.assigned_installer_names
                : (record.assigned_to_name ? [record.assigned_to_name] : []);
              const assistants = record.assigned_assistant_names || [];
              const noneAssigned = installers.length === 0 && assistants.length === 0;
              return (
                <>
                  {installers.length > 0 && (
                    <KV
                      label={installers.length > 1 ? "Installers" : "Installer"}
                      value={installers.join(", ")}
                    />
                  )}
                  {assistants.length > 0 && (
                    <KV
                      label={assistants.length > 1 ? "Assistants" : "Assistant"}
                      value={assistants.join(", ")}
                    />
                  )}
                  {!record.scheduled_date && noneAssigned && (
                    <div className="text-xs text-slate-500 italic">Not scheduled yet.</div>
                  )}
                </>
              );
            })()}
          </GlassPanel>

          {(record.location_type || record.location_address) && (
            <GlassPanel className="!p-5 space-y-2">
              <SectionHead icon={MapPin} title="Location" />
              {record.location_type && (
                <KV label="Where" value={record.location_type === "on_site" ? "On-site (in shop)" : "Off-site (customer location)"} />
              )}
              {record.location_address && <KV label="Address" value={record.location_address} />}
            </GlassPanel>
          )}

          {/* Signed T&Cs archive — only surfaces on real jobs where the
              customer has already signed, so office can download the PDF
              for their legal records. */}
          {record.main_status === "job" && record.terms_signed_at && (
            <GlassPanel className="!p-5 space-y-2" data-testid="job-detail-terms-signed">
              <SectionHead icon={ShieldCheck} title="Installation T&Cs signed" />
              <KV label="Signed by" value={record.terms_signed_name || "—"} />
              <KV label="Signed at" value={new Date(record.terms_signed_at).toLocaleString()} />
              {record.terms_signed_ip && <KV label="From IP" value={record.terms_signed_ip} />}
              <a
                href={`${API_BASE}/jobs/${record.id}/terms-pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="job-detail-terms-download"
                className="mt-2 inline-flex items-center gap-2 bg-emerald-500 text-black text-xs font-semibold rounded-full px-4 py-1.5 hover:bg-emerald-400 transition"
              >
                <Download className="w-3 h-3" /> Download signed T&amp;Cs PDF
              </a>
            </GlassPanel>
          )}

          {Array.isArray(record.materials) && record.materials.length > 0 && (
            <GlassPanel className="!p-5 space-y-2">
              <SectionHead icon={Package} title={`Materials (${record.materials.length})`} />
              <ul className="space-y-1 text-sm text-slate-300">
                {record.materials.map((m, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="truncate">{m.name || m.sku || "—"}</span>
                    <span className="text-slate-500 shrink-0">×{m.qty || 1}</span>
                  </li>
                ))}
              </ul>
            </GlassPanel>
          )}

          {Array.isArray(record.ptg_readings) && record.ptg_readings.length > 0 && (
            <GlassPanel className="!p-5 space-y-2">
              <SectionHead icon={Tag} title={`PTG readings (${record.ptg_readings.length})`} />
              <ul className="space-y-1 text-xs text-slate-400 max-h-40 overflow-y-auto">
                {record.ptg_readings.map((r, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="truncate">{r.panel || `Reading ${i + 1}`}</span>
                    <span className="text-slate-300">{r.microns} µm</span>
                  </li>
                ))}
              </ul>
            </GlassPanel>
          )}

          <GlassPanel className="!p-5">
            <SectionHead icon={ClipboardList} title="Overall pipeline" />
            <div className="mt-2">
              <StatusPipeline
                job={record}
                onChange={(updated) => setRecord(updated || record)}
                vertical
              />
            </div>
          </GlassPanel>
        </div>
      </div>
      {sendingInvoice && (
        <SendInvoiceModal
          invoice={sendingInvoice}
          onClose={() => setSendingInvoice(null)}
          onRefresh={loadInvoices}
        />
      )}
    </div>
  );
}

function BackLink({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="job-detail-back"
      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-400 hover:text-white transition"
    >
      <ArrowLeft className="w-3 h-3" /> Back to jobs
    </button>
  );
}

function SectionHead({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
      <Icon className="w-3 h-3" /> {title}
    </div>
  );
}

function KV({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="text-slate-500 min-w-[90px] text-[11px] uppercase tracking-wider">{label}</div>
      <div className="text-slate-200 flex-1 break-words">{value}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Payment detail panel                                                       */
/* -------------------------------------------------------------------------- */
/**
 * Sidebar payment breakdown for the Job Detail page. Mirrors the
 * `PaymentChip` on the Jobs list but with room to breathe: shows billed,
 * paid, and outstanding as a stack of key/value rows, plus a coloured
 * status pill for at-a-glance state.
 */
function PaymentDetailPanel({ summary }) {
  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const s = summary || {};
  let pillCls = "pill-muted";
  let pillLabel = "Unpaid";
  if (s.status === "paid") { pillCls = "pill-success"; pillLabel = "Paid in full"; }
  else if (s.status === "deposit") { pillCls = "pill-warning"; pillLabel = "Deposit paid"; }
  return (
    <div className="space-y-2" data-testid="job-detail-payment-summary">
      <div className="flex items-center justify-between">
        <span className={`pill ${pillCls} text-[10px]`} data-testid="job-detail-payment-status">{pillLabel}</span>
        {Number(s.invoice_count) > 0 && (
          <span className="text-[10px] uppercase tracking-widest text-slate-500">
            {s.invoice_count} invoice{s.invoice_count === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <KV label="Billed" value={money(s.billed)} />
      <KV label="Paid"   value={money(s.paid)} />
      {Number(s.outstanding || 0) > 0 && (
        <KV label="Outstanding" value={money(s.outstanding)} />
      )}
    </div>
  );
}
